import { useMemo } from 'react';
import { TreeNodeMetadata } from './types';

export type WbTreeNodeFlatItem<T> = {
  data: T;
  treeProps: TreeNodeMetadata;
};

/**
 * Prepares a flat list of tree nodes with props for WbTreeNode flat rendering.
 * Useful for rendering the tree in a virtualized list.
 *
 * @param roots - array of root nodes
 * @param getChildren - function to get the children of a node
 */
export function useWbTreeFlatList<T>(
  roots: T[],
  getChildren: (node: T) => T[],
): WbTreeNodeFlatItem<T>[] {
  return useMemo(() => {
    const result: WbTreeNodeFlatItem<T>[] = [];

    function dfs(
      node: T,
      depth: number = 0,
      siblings: T[] = [],
      index?: number,
      ancestorsLastChild: boolean[] = [],
    ) {
      const children = getChildren(node) || [];
      const isLastChild = index === siblings.length - 1;

      const updatedAncestorsLastChild =
        depth === 0 ? ancestorsLastChild : [...ancestorsLastChild, isLastChild];

      result.push({
        data: node,
        treeProps: {
          ancestorsLastChild: updatedAncestorsLastChild,
        },
      });

      children.forEach((child, idx) =>
        dfs(child, depth + 1, children, idx, updatedAncestorsLastChild),
      );
    }

    roots.forEach(root => dfs(root));

    return result;
  }, [roots, getChildren]);
}
